import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Target, Rocket, TrendingUp, Heart, Briefcase, Store, Landmark, HandHeart,
  Plus, Eye, Edit, BookOpen
} from "lucide-react";

const POD_ICONS = {
  startup: Rocket,
  scaling: TrendingUp,
  women: Heart,
  service: Briefcase,
  retail: Store,
  government: Landmark,
  nonprofit: HandHeart
};

export function MastermindPodsZone({
  user,
  pods,
  setSelectedPod,
  setShowEditPod,
  setShowCreatePod,
  openPodDetails,
  handleJoinPod,
  handleLeavePod,
  fetchWorkshopsAndPodcasts
}) {
  return (
    <div className="space-y-6">
      {/* How-to Instructions Card */}
      <Card className="bg-gradient-to-r from-[#39FF14]/10 to-neutral-900/50 border-[#39FF14]/20">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            <div className="w-10 h-10 bg-[#39FF14]/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5 text-[#39FF14]" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white mb-2">How to Use Mastermind Pods</h3>
              <ul className="text-sm text-neutral-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-[#39FF14] font-bold">1.</span>
                  <span><strong>Browse pods</strong> that match your business stage or industry</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#39FF14] font-bold">2.</span>
                  <span><strong>Join a pod</strong> to connect with like-minded entrepreneurs</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#39FF14] font-bold">3.</span>
                  <span><strong>Open the pod</strong> to see workshops, podcasts, and resources shared by consultants</span>
                </li>
              </ul>
            </div>
            {/* Consultant: Create Pod Button */}
            {user?.role === "consultant" && (
              <Button
                onClick={() => setShowCreatePod(true)}
                className="bg-[#39FF14] text-black hover:bg-[#32e612]"
                data-testid="create-pod-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Pod
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pods Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {pods.length > 0 ? pods.map((pod) => {
          const Icon = POD_ICONS[pod.pod_type] || Target;
          return (
            <Card
              key={pod.pod_id}
              className={`bg-neutral-800/50 border-neutral-700 ${pod.is_member ? "border-[#39FF14]/50" : ""}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#39FF14]/10 rounded-xl flex items-center justify-center">
                    <Icon className="w-6 h-6 text-[#39FF14]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white">{pod.name}</h3>
                    <p className="text-xs text-neutral-400">{pod.member_count || 0} members</p>
                  </div>
                  {pod.is_member && (
                    <Badge className="bg-[#39FF14] text-black">Joined</Badge>
                  )}
                </div>
                <p className="text-sm text-neutral-400 mb-4 line-clamp-2">{pod.description}</p>

                {/* Content preview */}
                {pod.content && pod.content.length > 0 && (
                  <div className="mb-4 p-2 bg-neutral-900/50 rounded-lg">
                    <p className="text-xs text-[#39FF14] mb-1">Latest Content:</p>
                    <p className="text-xs text-neutral-300 truncate">
                      {pod.content[0]?.title || "View inside"}
                    </p>
                  </div>
                )}

                {pod.is_member ? (
                  <div className="flex gap-2">
                    <Button
                      className="flex-1 bg-[#39FF14] text-black hover:bg-[#32e612]"
                      onClick={() => openPodDetails(pod)}
                      data-testid={`open-pod-${pod.pod_id}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Open Pod
                    </Button>
                    <Button
                      variant="ghost"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => handleLeavePod(pod.pod_id)}
                    >
                      Leave
                    </Button>
                  </div>
                ) : (
                  <Button
                    className="w-full bg-[#39FF14] text-black hover:bg-[#32e612]"
                    onClick={() => handleJoinPod(pod.pod_id)}
                    data-testid={`join-pod-${pod.pod_id}`}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Join Pod
                  </Button>
                )}

                {/* Consultant edit button */}
                {user?.role === "consultant" && (
                  <div className="mt-2 flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-1 text-neutral-400 hover:text-white"
                      onClick={() => { setSelectedPod(pod); setShowEditPod(true); }}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-neutral-400 hover:text-[#39FF14]"
                      onClick={() => { openPodDetails(pod); fetchWorkshopsAndPodcasts(); }}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Content
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        }) : (
          <div className="col-span-3 text-center py-12 text-neutral-500">
            <Target className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>No pods available yet</p>
            {user?.role === "consultant" && (
              <Button
                className="mt-4 bg-[#39FF14] text-black hover:bg-[#32e612]"
                onClick={() => setShowCreatePod(true)}
              >
                Create First Pod
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
